import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Loader2, LogOut } from 'lucide-react';
import Dialog from '../ui/Dialog';
import { runQuickCreate } from '../quickcreate/quickCreateStore';
import { toast } from '../../store/toastStore';
import SuggestionsList from './SuggestionsList';
import { useSuggestionsController } from './useWorkLogSuggestions';
import { documentationGap, hoursMinutes } from './documentationModel';
import TimersCloseList from '../attendance/TimersCloseList';
import { closeDayPrimary, closeRowsFromTimers, hasUncheckedTimers, type TimerCloseRow } from '../attendance/endOfShiftModel';
import { saveRowsProgress, stopCheckedTimers } from '../attendance/attendanceFlow';
import type { ActiveTimer } from '../../types';

interface CloseDayDialogProps {
  open: boolean;
  /** Closed without clocking out (×, Escape, or "רישום שעות"). */
  onCancel: () => void;
  /** Clock out now (the usual delayed clock-out with "ביטול"). */
  onClockOut: () => void;
  /** P-10e (D-85) — the employee's running timers (the area on top). */
  timers?: ActiveTimer[];
}

/**
 * "סגירת יום" (documentation brief, part 2, 2026-10-12): opened by
 * "החתמת יציאה" when there are suggestions or 15+ minutes of attendance
 * without work logs (`shouldOpenCloseDay`) — and, P-10e (D-85), whenever a
 * timer runs: ONE window, never a second one after it.
 *
 *   סגירת יום
 *   נוכחת 8:30 · תיעדת 6:10 · חסרות 2:20
 *   טיימרים פעילים
 *     ☑ עיצוב דף הבית  00:42  מה עשיתי [   ]  ⚪🔵🟣🟠🟢  [━━●───]
 *     ⚠ טיימר שלא מסומן ייעצר בכל זאת ביציאה, בלי תיעוד
 *   (the suggestions list, as before)
 *            [יציאה בלי להשלים]   [עצירה, אישור ויציאה]
 *
 * - The primary, in order: stops the CHECKED timers (each with its note; the
 *   task's status / bar saved), confirms every suggestion row left, then
 *   clocks out. A row that fails stays with its reason and the dialog stays
 *   open. Without suggestions it is "עצירה ויציאה"; without timers it is as
 *   before ("אישור ויציאה", or "רישום שעות" with only a gap).
 * - "יציאה בלי להשלים": clocks out as it is. The clock-out stops every timer
 *   still running, without notes (D-66: no timer without attendance) — the
 *   window warns about an unchecked timer.
 */
export default function CloseDayDialog({ open, onCancel, onClockOut, timers = [] }: CloseDayDialogProps) {
  const { t } = useTranslation();
  const controller = useSuggestionsController();
  const primaryRef = useRef<HTMLButtonElement>(null);
  const data = controller.data;
  const rows = controller.rows;
  const [timerRows, setTimerRows] = useState<TimerCloseRow[]>(() => closeRowsFromTimers(timers));
  const [stopping, setStopping] = useState(false);
  const busy = controller.bulkBusy || stopping;

  const gap = data ? documentationGap(data.attendance_minutes, data.documented_minutes) : 0;
  const facts = data ? [
    t('documentation.closeDay.attended', { time: hoursMinutes(data.attendance_minutes) }),
    t('documentation.closeDay.documented', { time: hoursMinutes(data.documented_minutes) }),
    ...(gap > 0 ? [t('documentation.closeDay.missing', { time: hoursMinutes(gap) })] : []),
  ] : [];
  const primary = closeDayPrimary({ timers: timerRows.length, suggestions: rows.length });

  /** Stop the checked timers and save their tasks. False = something failed (stay open). */
  const stopChecked = async (): Promise<boolean> => {
    if (timerRows.length === 0) return true;
    setStopping(true);
    try {
      const failed = await stopCheckedTimers(timerRows);
      for (const title of failed) toast('error', t('timer.closeList.stopFailed', { title }));
      if (failed.length > 0) return false;
      const progressFailed = await saveRowsProgress(timerRows);
      if (progressFailed.length > 0) toast('error', t('ticketDetail.statusChangeFailed'));
      // Stopped: they leave the list (an unchecked one stays, and the clock-out stops it).
      setTimerRows((cur) => cur.filter((r) => !r.checked));
      return true;
    } finally {
      setStopping(false);
    }
  };

  const confirmAndLeave = async () => {
    if (!(await stopChecked())) return;
    const outcome = await controller.acceptAll();
    if (outcome.failed === 0) onClockOut();
  };
  const stopAndLeave = async () => {
    if (await stopChecked()) onClockOut();
  };
  const logTime = () => {
    onCancel();
    runQuickCreate('worklog');
  };

  const primaryLabel = primary === 'stopConfirmAndLeave'
    ? t('documentation.closeDay.stopConfirmAndLeave')
    : primary === 'stopAndLeave'
      ? t('documentation.closeDay.stopAndLeave')
      : t('documentation.closeDay.confirmAndLeave');

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      title={t('documentation.closeDay.title')}
      icon={<LogOut size={20} strokeWidth={1.75} className="rtl-mirror shrink-0 text-accent-text" aria-hidden />}
      size="md"
      dismissible={!busy}
      initialFocus={primaryRef}
      returnFocus={false}
      panelProps={{ 'data-close-day': '' }}
      footer={(
        <>
          {primary === 'stopAndLeave' && gap > 0 && (
            <button type="button" onClick={logTime} disabled={busy} className="btn btn-ghost flex-1 sm:flex-none">
              {t('documentation.closeDay.logTime')}
            </button>
          )}
          <button type="button" onClick={onClockOut} disabled={busy} className="btn btn-secondary flex-1 sm:flex-none">
            {t('documentation.closeDay.leaveAnyway')}
          </button>
          {primary === 'logTime' ? (
            <button ref={primaryRef} type="button" onClick={logTime} className="btn btn-primary flex-1 sm:flex-none">
              {t('documentation.closeDay.logTime')}
            </button>
          ) : (
            <button
              ref={primaryRef}
              type="button"
              onClick={() => void (primary === 'stopAndLeave' ? stopAndLeave() : confirmAndLeave())}
              disabled={busy}
              aria-busy={busy || undefined}
              className="btn btn-primary flex-1 sm:flex-none"
              data-close-day-primary={primary}
            >
              {busy && <Loader2 size={16} className="animate-spin" aria-hidden="true" />}
              {primaryLabel}
            </button>
          )}
        </>
      )}
    >
      {facts.length > 0 && (
        <p className="mb-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm font-medium text-text tabular-nums" data-close-day-facts="">
          {facts.map((f, i) => (
            <span key={i} className="inline-flex items-center gap-2">
              {i > 0 && <span className="text-text-disabled" aria-hidden="true">·</span>}
              {f}
            </span>
          ))}
        </p>
      )}
      {timerRows.length > 0 && (
        <section className="mb-4" aria-label={t('documentation.closeDay.activeTimers')} data-close-day-timers="">
          <h3 className="mb-1 text-sm font-semibold text-text">{t('documentation.closeDay.activeTimers')}</h3>
          <TimersCloseList rows={timerRows} onChange={setTimerRows} disabled={busy} />
          {hasUncheckedTimers(timerRows) && (
            <p role="note" className="mt-2 flex items-start gap-1.5 text-xs text-warning-700" data-close-day-unchecked="">
              <AlertTriangle size={14} className="mt-px shrink-0" aria-hidden="true" />
              {t('documentation.closeDay.uncheckedWarning')}
            </p>
          )}
        </section>
      )}
      <SuggestionsList controller={controller} showAcceptAll={false} empty={t('documentation.closeDay.noSuggestions')} />
    </Dialog>
  );
}
